import { useEffect, useMemo, useState } from 'react';
import { catalogApi, matterApi, type InterlocutoryType } from '../../api';

type Props = {
  matterId: number | string;
  parentMatterTypeCode?: string;
  onClose: () => void;
  onCreated: (childId: string) => void;
};

const CATEGORY_LABELS: Record<InterlocutoryType['category'], string> = {
  pleadings_stage:        'Pleadings stage',
  pre_trial:              'Pre-trial / trial-prep',
  default_judgment:       'Default judgment & rescission',
  proceedings_management: 'Proceedings management',
  application_specific:   'Application-specific',
  post_judgment:          'Post-judgment',
};

export default function AddInterlocutoryModal({ matterId, parentMatterTypeCode, onClose, onCreated }: Props) {
  const [types, setTypes] = useState<InterlocutoryType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<InterlocutoryType | null>(null);
  const [reliefSought, setReliefSought] = useState('');
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    catalogApi.interlocutoryTypes(parentMatterTypeCode).then((r) => {
      setTypes(r.interlocutory_types || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [parentMatterTypeCode]);

  const grouped = useMemo(() => {
    const filtered = types.filter((t) =>
      !search.trim() || t.name.toLowerCase().includes(search.toLowerCase()) || t.code.includes(search.toLowerCase())
    );
    const out: Record<string, InterlocutoryType[]> = {};
    for (const t of filtered) (out[t.category] ||= []).push(t);
    return out;
  }, [types, search]);

  const create = async () => {
    if (!selected) return;
    setCreating(true);
    try {
      const r = await matterApi.createInterlocutory(matterId, {
        interlocutory_type_code: selected.code,
        relief_sought: reliefSought || undefined,
      });
      onCreated(r.id);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div className="row">
          <h3 className="grow" style={{ margin: 0 }}>New interlocutory application</h3>
          <button onClick={onClose}>×</button>
        </div>

        {loading ? (
          <div className="muted">Loading catalogue…</div>
        ) : !selected ? (
          <>
            <input
              type="text"
              placeholder="Search the 43-type catalogue…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%' }}
              autoFocus
            />
            <div style={{ maxHeight: 460, overflowY: 'auto' }}>
              {Object.entries(grouped).map(([cat, list]) => (
                <div key={cat} style={{ marginTop: 12 }}>
                  <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, padding: '6px 0' }}>
                    {CATEGORY_LABELS[cat as InterlocutoryType['category']] || cat}
                  </div>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {list.map((t) => (
                      <li
                        key={t.code}
                        onClick={() => setSelected(t)}
                        style={typeRow}
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-hover, #f6f6f6)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500 }}>{t.name}</div>
                          <div className="muted" style={{ fontSize: 12 }}>
                            {t.rule_citation || '—'}{t.trigger_text ? ` · ${t.trigger_text}` : ''}
                          </div>
                        </div>
                        {(t.suspends_parent_deadlines === true || t.suspends_parent_deadlines === 'true') && (
                          <span style={pill('warning')}>suspends parent</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="stack">
            <button onClick={() => setSelected(null)} style={{ alignSelf: 'flex-start' }}>← Back to catalogue</button>
            <div className="card">
              <div style={{ fontWeight: 600 }}>{selected.name}</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                {selected.rule_citation || '—'}{selected.source ? ` · ${selected.source}` : ''}
              </div>
              {selected.trigger_text && (
                <div style={{ fontSize: 13, marginTop: 8 }}>
                  <strong>Trigger:</strong> {selected.trigger_text}
                </div>
              )}
              {(selected.suspends_parent_deadlines === true || selected.suspends_parent_deadlines === 'true') && (
                <div style={{ fontSize: 13, marginTop: 8, color: 'var(--color-warning, #d97706)' }}>
                  ⚠ Opening this interlocutory will suspend the parent matter's pending deadlines.
                </div>
              )}
              {selected.appealable_on_grant && (
                <div style={{ fontSize: 12, marginTop: 8 }} className="muted">
                  Appealable on grant: <strong>{selected.appealable_on_grant}</strong> · on refusal: <strong>{selected.appealable_on_refusal || '—'}</strong>
                </div>
              )}
            </div>
            <label className="stack" style={{ gap: 4 }}>
              <span style={{ fontSize: 12 }}>Relief sought (free text)</span>
              <textarea
                rows={3}
                value={reliefSought}
                onChange={(e) => setReliefSought(e.target.value)}
                placeholder="What order is sought (e.g. 'Summary judgment in the sum of R250,000 plus interest')…"
                autoFocus
              />
            </label>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button onClick={onClose} disabled={creating}>Cancel</button>
              <button className="primary" onClick={create} disabled={creating}>
                {creating ? 'Creating…' : 'Create child matter'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function pill(tone: 'warning' | 'info'): React.CSSProperties {
  const color = tone === 'warning' ? 'var(--color-warning, #d97706)' : 'var(--color-info, #2563eb)';
  return {
    fontSize: 11, padding: '2px 6px', borderRadius: 8,
    border: `1px solid ${color}`, color, whiteSpace: 'nowrap', marginLeft: 8,
  };
}

const typeRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '10px 12px', borderRadius: 6, cursor: 'pointer',
};
const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};
const modal: React.CSSProperties = {
  background: 'var(--color-bg, #fff)', padding: 24, borderRadius: 8,
  width: 'min(640px, 92vw)', maxHeight: '88vh', overflowY: 'auto',
  display: 'flex', flexDirection: 'column', gap: 12,
};
